import React from 'react';
import Icon from 'components/AppIcon';

const VideoControls = ({
  isVideoEnabled,
  isAudioEnabled,
  isScreenSharing,
  isRecording,
  clientConsent,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onToggleRecording,
  onEndCall,
  onClientConsentChange,
  screenShareError
}) => {
  const handleScreenShare = () => {
    // Add confirmation for browser compatibility
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      alert('Screen sharing is not supported in this browser. Please use Chrome, Firefox, or Edge.');
      return;
    }
    
    onToggleScreenShare();
  };

  return (
    <div className="bg-surface border-t border-border px-6 py-4 flex-shrink-0">
      <div className="flex items-center justify-between">
        {/* Primary Controls */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onToggleAudio}
            className={`p-3 rounded-full transition-smooth ${
              isAudioEnabled 
                ? 'bg-secondary-100 hover:bg-secondary-200 text-text-primary' :'bg-error hover:bg-error-dark text-white'
            }`}
            title={isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
          >
            <Icon name={isAudioEnabled ? "Mic" : "MicOff"} size={20} />
          </button>

          <button
            onClick={onToggleVideo}
            className={`p-3 rounded-full transition-smooth ${
              isVideoEnabled 
                ? 'bg-secondary-100 hover:bg-secondary-200 text-text-primary' :'bg-error hover:bg-error-dark text-white'
            }`}
            title={isVideoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
          >
            <Icon name={isVideoEnabled ? "Video" : "VideoOff"} size={20} />
          </button>

          <div className="relative">
            <button
              onClick={handleScreenShare}
              className={`p-3 rounded-full transition-smooth ${
                isScreenSharing 
                  ? 'bg-primary text-white' 
                  : screenShareError 
                    ? 'bg-error-100 hover:bg-error-200 text-error border-2 border-error' :'bg-secondary-100 hover:bg-secondary-200 text-text-primary'
              }`}
              title={
                isScreenSharing 
                  ? 'Stop Screen Share' 
                  : screenShareError 
                    ? `Screen Share Error: ${screenShareError}` 
                    : 'Share Screen'
              }
            >
              <Icon 
                name={screenShareError ? "AlertTriangle" : "Monitor"} 
                size={20} 
              />
            </button>
            
            {/* Screen Share Status Indicator */}
            {isScreenSharing && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-white"></div>
            )}
            
            {screenShareError && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-error rounded-full border-2 border-white animate-pulse"></div>
            )}
          </div>
        </div>

        {/* Recording Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="flex items-center space-x-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={clientConsent}
                onChange={(e) => onClientConsentChange(e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span>Client consent for recording</span>
            </label>
          </div>

          <button
            onClick={onToggleRecording}
            disabled={!clientConsent && !isRecording}
            className={`flex items-center space-x-2 px-4 py-2 rounded-interactive transition-smooth ${
              isRecording 
                ? 'bg-error text-white hover:bg-error-dark' 
                : clientConsent 
                  ? 'bg-secondary-100 hover:bg-secondary-200 text-text-primary' :'bg-secondary-100 text-text-secondary cursor-not-allowed opacity-50'
            }`}
            title={isRecording ? 'Stop Recording' : 'Start Recording'}
          >
            <Icon name="Circle" size={16} className={isRecording ? 'animate-pulse' : ''} />
            <span className="text-sm font-medium">
              {isRecording ? 'Recording' : 'Record'}
            </span>
          </button>
        </div>

        {/* Secondary Controls */}
        <div className="flex items-center space-x-4">
          <button
            className="p-3 rounded-full bg-secondary-100 hover:bg-secondary-200 text-text-primary transition-smooth"
            title="Open Settings"
          >
            <Icon name="Settings" size={20} />
          </button>

          <button
            className="p-3 rounded-full bg-secondary-100 hover:bg-secondary-200 text-text-primary transition-smooth"
            title="More Options"
          >
            <Icon name="MoreHorizontal" size={20} />
          </button>

          <button
            onClick={onEndCall}
            className="px-6 py-2 bg-error hover:bg-error-dark text-white rounded-interactive transition-smooth font-medium"
          >
            End Call
          </button>
        </div>
      </div>

      {/* Screen Share Error Banner */}
      {screenShareError && (
        <div className="mt-3 p-3 bg-error-50 border border-error-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-error-dark">Screen Sharing Issue</p>
              <p className="text-xs text-error-600 mt-1">{screenShareError}</p>
              <div className="mt-2 flex items-center space-x-3 text-xs">
                <button
                  onClick={handleScreenShare}
                  className="text-primary hover:text-primary-dark underline"
                >
                  Try Again
                </button>
                <span className="text-text-secondary">•</span>
                <a 
                  href="https://support.google.com/chrome/answer/2693767" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark underline"
                >
                  Browser Help
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recording Status Bar */}
      {isRecording && (
        <div className="mt-3 p-3 bg-error-50 border border-error-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-error rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-error-dark">
              Consultation Recording in Progress
            </span>
          </div>
          <div className="text-xs text-error-600">
            Encrypted and stored securely • Auto-transcript enabled
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoControls;